import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  finalize,
  first,
  mapTo,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import {
  BatchMailing,
  CreateEditBatchItemBody,
  DepartureAttachmentItem,
  PartialReceiptsItemWithSelected,
} from '../../models';
import {
  BatchesParseStorageService,
  BatchesService,
  BatchMailingUpdateParams,
} from '../../services';
import { NavigatePartialReceiptsModalComponent } from '$modules/personal/modules/batch/features/batches/modals/navigate-partial-receipts-modal/navigate-partial-receipts-modal.component';
import { faPenSquare, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { deleteSymbols } from '$modules/personal/modules/batch';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AddAttachmentModalComponent } from '$modules/personal/modules/batch/features/batches/modals/add-attachment-modal/add-attachment-modal.component';
import { ConfirmDeleteModalComponent } from '$core/modal/confirm-delete/confirm-delete.component';
import { TranslateService } from '@ngx-translate/core';
import { triggerFormValidation } from '$core/helpers/trigger-form-validation';
import { focusFirstInvalidField } from '$core/helpers/focus-first-invaild-field';
import { HttpErrorResponse } from '@angular/common/http';
import { validateLocalStoragePerPage } from '$modules/personal/modules/batch/validateLocalStorageBatch';
import { EditeBatchDepartureModalComponent } from '$modules/personal/modules/batch/features/batches/modals/edite-batch-departure-modal/edite-batch-departure-modal.component';

@Component({
  selector: 'app-view-departure-attachments',
  templateUrl: './view-departure-attachments.component.html',
  styleUrls: ['./view-departure-attachments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewDepartureAttachmentsComponent implements OnInit, OnDestroy {
  public readonly faTrashAlt = faTrashAlt;
  public readonly deleteSymbols = deleteSymbols;
  public readonly faPenToSquare = faPenSquare;

  public isAttachmentLoading$ = new BehaviorSubject<boolean>(false);
  public batchDeparture$ = this.batchStorageService.batchDeparture$;
  public isFullScreen$ = this.batchStorageService.isFullScreen$;
  public departureForm: FormGroup;
  public activeTab = 2;
  public queryParams: Params = {} as Params;
  public departureBlankStatus$ = this.batchStorageService.departureBlankStatus$;
  @ViewChild('formEl', { static: false })
  public formEl: ElementRef<HTMLFormElement>;

  constructor(
    private readonly modalService: BsModalService,
    private readonly batchesService: BatchesService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly renderer: Renderer2,
    private readonly fb: FormBuilder,
    private readonly batchStorageService: BatchesParseStorageService,
    private readonly cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  @HostListener('document:keydown.escape')
  public onEscapeKeyPress() {
    if (this.isFullScreen$.getValue()) {
      this.toggleFullScreen();
    }
  }

  public ngOnInit() {
    this.listenToRouter();
    this.loadData();
  }

  public initForm() {
    this.departureForm = this.fb.group({
      declared_value: [
        this.batchDeparture$.getValue().addons.declared_value || null,
        this.batchDeparture$.getValue().is_declared_value
          ? [Validators.required, Validators.min(1), Validators.max(999999999)]
          : [],
      ],
      cash_on_delivery: [
        this.batchDeparture$.getValue().addons.cash_on_delivery,
      ],
      weight: [this.batchDeparture$.getValue().weight, [Validators.required]],
    });

    if (!this.batchDeparture$.getValue().is_declared_value) {
      this.departureForm.get('declared_value').disable();
    }
  }

  private listenToDeparturesChanges() {
    this.batchDeparture$
      .pipe(
        distinctUntilChanged(
          (prev, curr) =>
            prev.addons.cash_on_delivery === curr.addons.cash_on_delivery
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: (res) => {
          this.departureForm.patchValue({
            declared_value:
              res && res.is_declared_value ? res.addons.declared_value : '',
            cash_on_delivery: res.addons.cash_on_delivery,
          });
        },
      });
  }

  private listenToForm() {
    this.departureForm.valueChanges.pipe(distinctUntilChanged()).subscribe({
      next: (res) => {
        this.calculate();
        this.departureForm
          .get('declared_value')
          .setValidators(this.declaredValueValidator(res.cash_on_delivery));
      },
    });
  }

  private calculate() {
    const batchId = this.activatedRoute.snapshot.params['id'];
    const batchMailing = this.batchDeparture$.getValue();
    const body: CreateEditBatchItemBody = {
      recipient_contact_widget_data:
        batchMailing.recipient_contact_widget_data || null,
      notification: batchMailing.notification,
      s10code: batchMailing.s10code,
      notification_s10code: batchMailing.notification_s10code,
      weight: this.departureForm.get('weight').value,
      addons: {
        ...batchMailing.addons,
        declared_value: this.departureForm.get('declared_value').value,
      },
    };

    this.batchesService
      .calculate(batchId, body)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (resp) => {
          if (resp) {
            this.batchDeparture$.next({
              ...this.batchDeparture$.getValue(),
              cost: resp.cost,
            });
          }
        },
        error: (resp: HttpErrorResponse) => {
          if (resp.error.errors) {
            Object.entries(resp.error.errors).forEach(([key, value]) => {
              if (key.includes('weight')) {
                this.departureForm.get('weight').setErrors(value);
              }
              if (key.includes('declared_value')) {
                this.departureForm.get('declared_value').setErrors(value);
              }
            });
          }
          this.cdr.detectChanges();
        },
      });
  }

  private declaredValueValidator(totalCashOnDelivery: number): ValidatorFn {
    return (
      declaredValueControl: FormControl
    ): { [key: string]: any } | null => {
      const declaredValue = declaredValueControl.value;

      if (declaredValue >= totalCashOnDelivery) {
        return null;
      }

      return {
        error:
          'BATCH_MAILING.BATCHES_TAB.CREATE_TABLE.VALIDATION_DECLARE_VALUE',
      };
    };
  }

  private loadData() {
    const batchId = this.activatedRoute.snapshot.params['id'] as string;
    const mailingId = this.activatedRoute.snapshot.queryParams[
      'departureId'
    ] as string;

    this.batchesService
      .showMailing({ batchId, mailingId })
      .pipe(
        tap((batchDeparture) => {
          this.batchDeparture$.next(batchDeparture);

          if (batchDeparture.document) {
            this.departureBlankStatus$.next(batchDeparture.document.status);
          }
        }),
        tap(() => {
          this.initForm();
          this.listenToForm();
          this.listenToDeparturesChanges();
        }),
        switchMap(() => this.handleNavigateModal()),
        switchMap((resp) => this.handleAttachmentModal(batchId, resp)),
        switchMap((resp) =>
          this.fetchDepartureAndAttachments(batchId, mailingId, resp)
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: (
          params: {
            batchDeparture: BatchMailing;
            batchDepartureAttachments: DepartureAttachmentItem[];
          } | null
        ) => {
          if (params) {
            this.batchStorageService.batchDepartureAttachments =
              params.batchDepartureAttachments;
            this.batchDeparture$.next(params.batchDeparture);

            if (params.batchDeparture.document) {
              this.departureBlankStatus$.next(
                params.batchDeparture.document.status
              );
            }
          }
        },
      });
  }

  private handleNavigateModal(): Observable<'confirm' | 'decline' | 'close'> {
    if (this.router.url.includes('created')) {
      this.modalService.show(NavigatePartialReceiptsModalComponent, {
        ignoreBackdropClick: true,
        class: 'modal-dialog-centered',
      });
      return this.modalService.onHidden.pipe(first());
    }
    return of(null);
  }

  private handleAttachmentModal(
    batchId: string,
    resp: 'confirm' | 'decline' | 'close'
  ): Observable<'success' | 'close' | null> {
    if (this.router.url.includes('created')) {
      if (resp === 'confirm') {
        this.modalService.show(AddAttachmentModalComponent, {
          initialState: {
            batchId: this.activatedRoute.snapshot.params['id'],
            batchDepartureId: this.batchDeparture$.getValue().id.toString(),
          },
          ignoreBackdropClick: false,
          class: 'modal-dialog-centered modal-lg',
        });

        return this.modalService.onHidden.pipe(first());
      }

      if (resp === 'decline') {
        let perPage = 15;

        if (localStorage.getItem('batch-departures-per-page')) {
          perPage = validateLocalStoragePerPage('batch-departures-per-page');
        }

        this.router.navigate([`personal/batch/batches/${batchId}/view-batch`], {
          queryParams: {
            perPage,
          },
        });
      }
    }

    return of(null);
  }

  private fetchDepartureAndAttachments(
    batchId: string,
    mailingId: string,
    resp: 'success' | 'close'
  ): Observable<{
    batchDeparture: BatchMailing;
    batchDepartureAttachments: DepartureAttachmentItem[];
  }> {
    if (resp === 'success') {
      return forkJoin({
        batchDeparture: this.batchesService.showMailing({ batchId, mailingId }),
        batchDepartureAttachments: this.batchesService.getDepartureAttachments(
          batchId,
          mailingId
        ),
      });
    }
    return of(null);
  }

  private listenToRouter() {
    const batchId = this.activatedRoute.snapshot.params['id'];

    this.activatedRoute.queryParams
      .pipe(
        tap((res) => {
          this.queryParams = res;
          if (this.queryParams && this.queryParams.activeTab) {
            this.setActiveTab(1);
          }
        }),
        switchMap(() => {
          return this.batchesService.getDepartureAttachments(
            batchId,
            this.queryParams.departureId,
            this.queryParams.search
          );
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (res) => {
          this.batchStorageService.batchDepartureAttachments = res;
        },
      });
  }

  public confirmDeleteDeparture() {
    this.modalService.show(ConfirmDeleteModalComponent, {
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered',
      initialState: {
        title: this.translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETE_DEPARTURE'
        ),
        subTitle: this.translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.CONFIRM_DELETING_DEPARTURE'
        ),
        submitBtnClass: 'button--filled',
        cancelBtnClass: 'button--text',
      },
    });

    this.modalService.onHidden
      .pipe(
        first(),
        filter((res) => res === 'delete')
      )
      .subscribe({
        next: () => this.deleteDeparture(),
      });
  }

  private deleteDeparture() {
    const batchId = this.activatedRoute.snapshot.params['id'];
    const departureId = this.activatedRoute.snapshot.queryParams['departureId'];

    this.batchesService
      .removeMailings(batchId, [Number(departureId)])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () =>
          this.router.navigateByUrl(
            `personal/batch/batches/${batchId}/view-batch`
          ),
      });
  }

  public toggleFullScreen() {
    this.batchStorageService.toggleFullScreen(this.renderer);
  }

  public setActiveTab(activeTab: number) {
    this.activeTab = activeTab;
  }

  public navigateToEditDeparturePage() {
    const id = this.activatedRoute.snapshot.params['id'];
    return this.router.navigate([`personal/batch/batches/${id}/edit`], {
      queryParams: {
        departureId: this.queryParams.departureId,
      },
    });
  }

  public backToViewBatch() {
    const id = this.activatedRoute.snapshot.params['id'];
    let perPage = 15;

    if (localStorage.getItem('batch-departures-per-page')) {
      perPage = validateLocalStoragePerPage('batch-departures-per-page');
    }

    return this.router.navigate([`personal/batch/batches/${id}/view-batch`], {
      queryParams: {
        departureId: this.queryParams.departureId,
        perPage,
      },
    });
  }

  public saveDeparture() {
    if (this.departureForm.invalid) {
      triggerFormValidation(this.departureForm);
      focusFirstInvalidField(this.formEl);
      return;
    }

    const batchId = this.activatedRoute.snapshot.params['id'];
    const mailingId = String(this.batchDeparture$.getValue().id);
    const initialBatch = this.batchDeparture$.getValue();

    const params: BatchMailingUpdateParams = {
      batchId,
      mailingId,
      weight: this.departureForm.get('weight').value,
      notification: initialBatch.notification,
      notification_s10code: initialBatch.notification_s10code,
      s10code: initialBatch.s10code,
      recipient_contact_widget_data: initialBatch.recipient_contact_widget_data
        ? initialBatch.recipient_contact_widget_data
        : null,
      addons: {
        ...initialBatch.addons,
        declared_value: initialBatch.addons.declared_value
          ? this.departureForm.get('declared_value').value
          : null,
      },
    };

    this.batchesService
      .updateMailing(params)
      .pipe(
        switchMap((batchDeparture) => {
          this.modalService.show(EditeBatchDepartureModalComponent, {
            ignoreBackdropClick: false,
            class: 'modal-dialog-centered',
            initialState: {
              batchDeparture,
            },
          });

          return this.modalService.onHide.pipe(take(1), mapTo(true));
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => this.backToViewBatch(),
      });
  }

  public async onQueryParamsChange(searchParams: { search: string }) {
    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge',
      queryParams: { ...this.queryParams, ...searchParams },
    });
  }

  public confirmDeleteAttachments(ids: string[]) {
    const batchId = this.activatedRoute.snapshot.params['id'];
    const mailingId = this.activatedRoute.snapshot.queryParams['departureId'];

    this.modalService.show(ConfirmDeleteModalComponent, {
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered',
      initialState: {
        title: this.translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETE_ATTACHMENTS'
        ),
        subTitle: this.translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.CONFIRM_DELETING_ATTACHMENT'
        ),
        submitBtnClass: 'button--filled',
        cancelBtnClass: 'button--text',
      },
    });

    this.modalService.onHidden
      .pipe(
        first(),
        filter((res) => res === 'delete'),
        switchMap(() => {
          this.isAttachmentLoading$.next(true);
          return this.batchesService.deleteDepartureAttachments(
            batchId,
            mailingId,
            { ids }
          );
        }),
        switchMap(() => {
          return forkJoin({
            batchDeparture: this.batchesService.showMailing({
              batchId,
              mailingId,
            }),
            batchDepartureAttachments:
              this.batchesService.getDepartureAttachments(batchId, mailingId),
          });
        }),
        finalize(() => this.isAttachmentLoading$.next(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (
          params: {
            batchDeparture: BatchMailing;
            batchDepartureAttachments: PartialReceiptsItemWithSelected[];
          } | null
        ) => {
          if (
            params &&
            params.batchDeparture &&
            params.batchDepartureAttachments
          ) {
            this.batchStorageService.batchDepartureAttachments =
              params.batchDepartureAttachments;
            this.batchDeparture$.next(params.batchDeparture);
            this.departureForm.get('declared_value').markAsTouched();

            if (params.batchDeparture.document) {
              this.departureBlankStatus$.next(
                params.batchDeparture.document.status
              );
            }
          }
        },
      });
  }

  public ngOnDestroy(): void {}
}
